import type { CSSObject } from '@emotion/react'
import { Color, FocusIndicator, Size, Spacing, Typography } from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lapidary'
import type { StyleProps } from './types'

const shroudInset = Spacing.S * 1.5

export const container = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N000,
  alignItems: 'center',
  borderRadius: Size.Rounding,
  display: 'flex',
  flexDirection: 'column',
  inset: shroudInset,
  // ensure there's always some padding on the sides, even when the viewport is
  // very narrow or very zoomed-in
  paddingInline: Spacing.S * 1.5,
  position: 'absolute',

  '&:focus': {
    ...FocusIndicator[theme],
  },
})

export const content = ({ theme }: StyleProps): CSSObject => ({
  color: Color[theme].Text.Default,
  ...Typography.T500,
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  // push text away from the scrollbar
  paddingInlineEnd: Spacing.M,
  ...modernWithFallback(
    [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
    'overflow-block: auto'
  ),

  a: {
    color: Color[theme].Text.Accent,
    borderRadius: Size.Rounding,
  },
  '&:focus, a:focus': {
    ...FocusIndicator[theme],
  },
  h2: {
    ...Typography.H300,
  },
  h3: {
    ...Typography.H100,
  },
})

export const footer = ({ theme }: StyleProps): CSSObject => ({
  borderBlockStart: `3px solid ${Color[theme].Translucent.A08}`,
  display: 'flex',
  justifyContent: 'center',
  paddingBlock: Spacing.M,
})

export const heading = ({ theme }: StyleProps): CSSObject => ({
  borderBlockEnd: `3px solid ${Color[theme].Translucent.A08}`,
  color: Color[theme].Text.Default,
  ...Typography.H500,
  display: 'flex',
  justifyContent: 'center',
  margin: 0,
  paddingBlockEnd: Spacing.S * 1.5,
  paddingBlockStart: Spacing.L,
})

export const shroud: CSSObject = {
  backgroundColor: Color.dark.Translucent.N72,
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  inset: 0,
  position: 'fixed',
  zIndex: 5,
}

export const sizedColumn: CSSObject = {
  maxBlockSize: `calc(100vb - (${shroudInset}px * 2))`,
  display: 'flex',
  flexDirection: 'column',
  maxInlineSize: Spacing.XL * 12.5,
}
